import React, {Suspense, useState} from 'react';
import {Link} from 'react-router-dom';
import {Canvas} from '@react-three/fiber';
import {OrbitControls, TransformControls} from '@react-three/drei';
import ProjectStar from '../universe/utility/ProjectStar';
import {useProjects} from '../../ProjectsContext';
import * as style from './adminStyles';

const round = (n) => Math.round(n * 100) / 100;

function MoveHandle({position, snap, onChange}) {
    const [anchor, setAnchor] = useState(null);

    return (
        <>
            <group ref={setAnchor} position={position}/>
            {anchor && (
                <TransformControls
                    object={anchor}
                    mode="translate"
                    translationSnap={snap ? 0.5 : null}
                    onObjectChange={() => onChange(anchor.position.toArray().map(round))}
                />
            )}
        </>
    );
}

export default function UniverseEditor() {
    const {projects, saveAll} = useProjects();
    const [draft, setDraft] = useState({});
    const [selectedId, setSelectedId] = useState(null);
    const [snap, setSnap] = useState(false);
    const [saved, setSaved] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const editable = projects.filter((p) => p.images?.[0] ?? p.image);
    const posOf = (p) => draft[p.id] ?? p.position;
    const selected = editable.find((p) => p.id === selectedId) ?? null;

    const dirty = editable.filter(
        (p) => draft[p.id] && draft[p.id].some((v, i) => v !== p.position[i])
    );

    const handleChange = (id, position) => {
        setDraft((d) => ({...d, [id]: position}));
        setSaved(false);
    };

    const handleSave = async () => {
        const nextProjects = projects.map((p) =>
            draft[p.id] ? {...p, position: draft[p.id]} : p
        );

        setSaving(true);
        setError('');
        try {
            await saveAll(nextProjects);
            setDraft({});
            setSaved(true);
        } catch (err) {
            setError(err.message || 'Speichern fehlgeschlagen.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={style.rootStyleUniversum}>
            <Canvas camera={{position: [0, 2, 16], fov: 60}}>
                <color attach="background" args={['#ffffff']}/>
                <ambientLight intensity={0.6}/>
                <gridHelper args={[60, 60, '#d5d5d5', '#ececec']} position={[0, -6, 0]}/>

                <Suspense fallback={null}>
                    {editable.map((p) => (
                        <ProjectStar
                            key={p.id}
                            project={{...p, position: posOf(p)}}
                            isSelected={p.id === selectedId}
                            onSelect={() => setSelectedId(p.id)}
                        />
                    ))}
                </Suspense>

                {selected && (
                    <MoveHandle
                        key={selected.id}
                        position={posOf(selected)}
                        snap={snap}
                        onChange={(pos) => handleChange(selected.id, pos)}
                    />
                )}

                <OrbitControls makeDefault enablePan minDistance={3} maxDistance={60}/>
            </Canvas>

            <div style={style.panelStyleUniversim}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <h1 style={{...style.h1, fontSize: '20px'}}>Universum anordnen</h1>
                    <Link to="/intern" style={{...style.button, padding: '6px 14px'}}>Zurück</Link>
                </div>

                <p style={style.hint}>
                    Stern anklicken, dann an den Pfeilen ziehen. Kamera: ziehen = drehen, Rechtsklick
                    (bzw. Zwei-Finger-Wischen) = verschieben, Scrollen = zoomen.
                </p>

                <div style={{margin: '12px 0'}}>
                    {editable.map((p) => (
                        <button
                            key={p.id}
                            onClick={() => setSelectedId(p.id)}
                            style={{
                                ...style.listItemStyle,
                                background: p.id === selectedId ? '#f0f0f0' : '#fff',
                                borderColor: p.id === selectedId ? '#111' : '#eaeaea',
                            }}
                        >
                            <span style={{fontWeight: 600}}>{p.title}</span>
                            <span style={{fontSize: '12px', color: '#777'}}>
                {posOf(p).map((v) => round(v)).join(', ')}
              </span>
                        </button>
                    ))}
                </div>

                <label style={{fontSize: '14px', display: 'flex', gap: '8px', alignItems: 'center'}}>
                    <input type="checkbox" checked={snap} onChange={(e) => setSnap(e.target.checked)}/>
                    Am Raster einrasten (0,5)
                </label>

                <div style={{display: 'flex', gap: '8px', marginTop: '16px'}}>
                    <button style={style.button} onClick={() => setDraft({})} disabled={dirty.length === 0}>
                        Zurücksetzen
                    </button>
                    <button
                        style={{...style.primaryButton, opacity: dirty.length === 0 || saving ? 0.4 : 1}}
                        onClick={handleSave}
                        disabled={dirty.length === 0 || saving}
                    >
                        {saving ? 'Speichert…' : `Speichern${dirty.length > 0 ? ` (${dirty.length})` : ''}`}
                    </button>
                </div>

                {saved && !error && (
                    <p style={{...style.hint, color: '#2e7d32'}}>Gespeichert.</p>
                )}
                {error && <p style={{...style.hint, color: '#c0392b'}}>{error}</p>}
            </div>
        </div>
    );
}
